<template>
  <!-- 进行中=倒计时 / 已截止=公布时间 / 已结束=结果文案；未开始无页脚 -->
  <PredictCountdown
    v-if="status === PREDICT_STATUS.ONGOING"
    :deadline="deadline"
    :label="$t('countdownLabel')"
    :day-unit="$t('unitDay')"
    :hour-unit="$t('unitHour')"
    :minute-unit="$t('unitMinute')"
    :second-unit="$t('unitSecond')"
    @finish="emit('countdown-finish')"
  />
  <p
    v-else-if="status === PREDICT_STATUS.CLOSED"
    class="text-center text-[#7a7a7a] text-[28px]"
  >
    {{ $t('announceLabel')}}
    <span class="font-bold text-[#FF7D00]">{{ formattedResolveAt }}</span>
  </p>
  <p
    v-else-if="status === PREDICT_STATUS.SETTLING"
    class="text-center text-[#7a7a7a] text-[28px]"
  >
    {{ $t('settlingLabel') }}
  </p>
  <p
    v-else-if="status === PREDICT_STATUS.SETTLED"
    class="text-center font-medium text-[28px]"
    :class="[
      result === PREDICT_RESULT.WIN ? 'text-[#FF7D00]' : 'text-[#999999]',
    ]"
  >
    {{ resultText }}
  </p>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import { useFegoPageInfo } from '@ark/web-shared/use';
import { dayjs, getDefaultTimezone } from '@ark/dayjs/frontend';
import { PREDICT_RESULT, PREDICT_STATUS } from '../../shared/types';
import type { PredictResult, PredictStatus } from '../../shared/types';
import PredictCountdown from './countdown.vue';
import { useWording } from '../hooks/useWording';
import locales from '../locales';

// 中文 lang 值（zh-CN / zh-HK 对应 0 / 1）
const __sfc_main = {};
__sfc_main.props = {
  status: {
    key: "status",
    required: true,
    type: null
  },
  deadline: {
    key: "deadline",
    required: true,
    type: Number
  },
  resolveAt: {
    key: "resolveAt",
    required: false,
    type: [Number, null],
    default: null
  },
  result: {
    key: "result",
    required: false,
    type: null,
    default: null
  },
  winText: {
    key: "winText",
    required: false,
    type: String,
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const ZH_LANGS = new Set([0, 1]);

  // IANA 时区 → 展业时区缩写（dayjs `z` token 对无 DST 时区仅返回 GMT+x，需自行映射）
  const TIMEZONE_ABBR_MAP: Record<string, string> = {
    'Asia/Hong_Kong': 'HKT',
    'America/New_York': 'ET',
    'Australia/Sydney': 'AET',
    'Asia/Singapore': 'SGT',
    'Asia/Kuala_Lumpur': 'MYT',
    'Asia/Tokyo': 'JST',
    'Asia/Bangkok': 'ICT',
    'Asia/Riyadh': 'AST',
    'Pacific/Auckland': 'NZST'
  };
  const emit = __ctx.emit;
  const props = __props;
  const {
    useI18n,
    useLang
  } = fegoUse;
  const $t = useI18n(locales);
  const {
    lang
  } = useLang();
  const {
    isPC
  } = useFegoPageInfo();
  const formattedResolveAt = computed(() => {
    if (!props.resolveAt) return '';
    const tz = getDefaultTimezone();
    const abbr = TIMEZONE_ABBR_MAP[tz] || '';
    const format = ZH_LANGS.has(lang) ? 'M月D日 HH:mm' : 'MMM D HH:mm';
    return dayjs(props.resolveAt).format(format) + (abbr ? ` ${abbr}` : '');
  });
  const {
    pick
  } = useWording();
  const resultText = computed(() => {
    if (props.result === PREDICT_RESULT.WIN) return props.winText || pick($t, 'resultWin', 'resultWinPredict');
    if (props.result === PREDICT_RESULT.LOSE) return pick($t, 'resultLose', 'resultLosePredict');
    if (props.result === PREDICT_RESULT.PUSH) return $t('resultPush');
    return pick($t, 'resultNotJoined', 'resultNotJoinedPredict');
  });
  return {
    PREDICT_RESULT,
    PREDICT_STATUS,
    emit,
    $t,
    formattedResolveAt,
    resultText
  };
};
__sfc_main.components = Object.assign({
  PredictCountdown
}, __sfc_main.components);
export default __sfc_main;
</script>
